import { useState, useEffect } from "react";
import {
  PlusIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  CalendarIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { Database } from "../types/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import PlanGatedPage from "../components/PlanGatedPage";
import { useStudioPlan } from "../hooks/useStudioPlan";
import DOMPurify from 'dompurify';

type FormRow = Database["public"]["Tables"]["forms"]["Row"];

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  formName: 100,
  formType: 50,
  formHtml: 50000, // Allow larger HTML for forms
  testName: 100,
  testEmail: 254,
  testPhone: 20,
  testMessage: 2000,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Enhanced input sanitization function (matching PublicWaiver.tsx approach)
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For form HTML, allow more formatting but still remove dangerous content
  if (fieldType === 'formHtml') {
    // Remove dangerous patterns but allow basic formatting
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    // Don't trim HTML to preserve formatting
  } else if (fieldType === 'formName') {
    // For form names, preserve spaces but remove dangerous content
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // DON'T trim for form names - preserve user intent
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function (matching PublicWaiver.tsx approach)
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'formName':
      // Form name should not be empty and should be reasonable length
      // Check for dangerous patterns instead of restrictive allowed characters
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 1 && sanitized.length <= MAX_LENGTHS.formName;
    case 'formHtml':
      // Form HTML should not be empty
      return sanitized.length >= 1 && sanitized.length <= MAX_LENGTHS.formHtml;
    case 'testEmail':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'testPhone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'testName':
    case 'testMessage':
      // Check for dangerous patterns instead of restrictive allowed characters
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Enhanced security event logging (matching PublicWaiver.tsx approach)
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Forms Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

export default function Forms() {
  const { session, getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { plan } = useStudioPlan();
  const formLimit = plan?.capabilities.maxWebsiteForms ?? 5;
  
  // Enhanced security-related state (matching PublicWaiver.tsx)
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [forms, setForms] = useState<FormRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingForm, setViewingForm] = useState<FormRow | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", type: "", html: "" });
  const [formFields, setFormFields] = useState({
    name: true,
    email: true,
    phone: true,
    message: true,
    smsConsent: true, // Now mandatory
  });

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // State for live preview customization
  const [previewTheme, setPreviewTheme] = useState("light");
  const [previewColor, setPreviewColor] = useState("#667eea");
  const [previewButtonText, setPreviewButtonText] = useState("#ffffff");
  const [lightBgColor, setLightBgColor] = useState("#ffffff");
  const [darkBgColor, setDarkBgColor] = useState("#1a1a1a");

  const [formToDelete, setFormToDelete] = useState<FormRow | null>(null);
  const defaultCSS = "/* ADD CUSTOM CSS HERE */";

  // Auto appointment SMS setting
  const [autoAppointmentSmsEnabled, setAutoAppointmentSmsEnabled] = useState<boolean>(true);

  // Enhanced CSRF token fetching (matching PublicWaiver.tsx approach)
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Helper function to fetch individual form with full content
  const fetchFormWithContent = async (
    formId: string
  ): Promise<FormRow | null> => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return null;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/forms/${studioUser.studio_id}/${formId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch form");
      }
      return await response.json();
    } catch (err) {
      return null;
    }
  };





  function generateFormHTML(fields: typeof formFields) {
    // Generate secure form HTML for iframe content with enhanced security
    let html = `<form class="contact-form" id="secureForm">\n`;

    if (fields.name)
      html += `  <div class="form-group">
    <label for="name">Student Name *</label>
    <input type="text" id="name" name="name" required maxlength="${MAX_LENGTHS.testName}" autocomplete="name">
  </div>\n`;

    if (fields.email)
      html += `  <div class="form-group">
    <label for="email">Email *</label>
    <input type="email" id="email" name="email" required maxlength="${MAX_LENGTHS.testEmail}" autocomplete="email">
    <div class="error-message" id="email-error" style="color: #dc2626; font-size: 14px; margin-top: 4px; display: none;"></div>
  </div>\n`;

    if (fields.phone)
      html += `  <div class="form-group">
    <label for="phone">Phone *</label>
    <input type="tel" id="phone" name="phone" maxlength="10" autocomplete="tel" pattern="[0-9]*" inputmode="numeric" placeholder="8885559999" oninput="this.value = this.value.replace(/[^0-9]/g, ''); if(/[^0-9]/.test(event.data)) { showPhoneError(); }">
    <div class="error-message" id="phone-error" style="color: #dc2626; font-size: 14px; margin-top: 4px; display: none;">No special characters allowed. Please use only numbers.</div>
  </div>\n`;

    if (fields.smsConsent)
      html += `  <div class="form-group">
    <label class="checkbox-label">
      <input type="checkbox" id="smsConsent" name="smsConsent" required>
      <span class="checkmark"></span>
      I consent to receive text messages and promotional communications from {{STUDIO_NAME}} at the phone number provided. Message and data rates may apply. Reply STOP to opt out.
    </label>
  </div>\n`;

    if (fields.message)
      html += `  <div class="form-group">
    <label for="message">Message</label>
    <textarea id="message" name="message" rows="4" maxlength="${MAX_LENGTHS.testMessage}"></textarea>
  </div>\n`;

    // Enhanced security measures (honeypot, time check, and CSRF token)
    html += `  <div class="form-group" style="display: none;">
    <label for="website">Website</label>
    <input type="text" id="website" name="website" autocomplete="off" tabindex="-1" aria-hidden="true">
  </div>\n`;

    html += `  <input type="hidden" id="formStartTime" name="formStartTime" value="">\n`;
    html += `  <input type="hidden" id="csrfToken" name="csrfToken" value="">\n`;

    html += `  <div class="form-group">
    <button type="submit" id="submitBtn">Submit</button>
  </div>
</form>

<script>


// Secure form handling with enhanced security measures
(function() {
  'use strict';
  
  const apiUrl = 'PLACEHOLDER_API_URL';
  let isSubmitting = false;
  let submissionAttempts = 0;
  const MAX_ATTEMPTS = 3;
  
  // Initialize form security
  function initializeFormSecurity() {
    const form = document.getElementById('secureForm');
    if (!form) return;
    
    // Set form start time for time-based validation
    const startTimeInput = document.getElementById('formStartTime');
    if (startTimeInput) {
      startTimeInput.value = Date.now().toString();
    }
    
    // Set CSRF token if available
    const csrfInput = document.getElementById('csrfToken');
    if (csrfInput && window.csrfToken) {
      csrfInput.value = window.csrfToken;
    }
    
    // Add form event listeners
    form.addEventListener('submit', handleFormSubmission);
    
    // Add input validation listeners
    addInputValidation();
  }
  
  // Add client-side input validation
  function addInputValidation() {
    const emailInput = document.getElementById('email');
    if (emailInput) {
      emailInput.addEventListener('input', validateEmail);
      emailInput.addEventListener('blur', validateEmail);
    }
    
    // Phone formatting is handled via inline oninput attribute
  }
  

  
  // Email validation
  function validateEmail(event) {
    const input = event.target;
    const errorDiv = document.getElementById('email-error');
    const email = input.value.trim();
    
    // Clear previous styling
    input.style.borderColor = '';
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
    
    if (email === '') {
      return; // Don't validate empty field while typing
    }
    
    // Enhanced email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      input.style.borderColor = '#dc2626';
      if (errorDiv) {
        errorDiv.textContent = 'Please enter a valid email address';
        errorDiv.style.display = 'block';
      }
    } else {
      input.style.borderColor = '#10b981';
    }
  }
  
  // Enhanced form submission handling
  async function handleFormSubmission(e) {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Check submission attempts
    if (submissionAttempts >= MAX_ATTEMPTS) {
      showError('Too many submission attempts. Please try again later.');
      return;
    }
    
    const form = e.target;
    const submitButton = document.getElementById('submitBtn');
    
    // Security validations
    if (!validateFormSecurity(form)) {
      return;
    }
    
    isSubmitting = true;
    submissionAttempts++;
    
    // Show loading state
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.innerHTML = '<div class="loading"></div> Submitting...';
    }
    
    try {
      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());
      
      // Extract formId from current URL
      const urlPath = window.location.pathname;
      const formId = urlPath.split('/').pop();
      
      // Submit form data
      const response = await fetch(\`\${apiUrl}/api/supabase/forms/submit/\${formId}\`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Submission failed');
      }
      
      // Show success message
      showSuccess();
      
    } catch (error) {
      showError(error.message || 'Submission failed. Please try again.');
    } finally {
      // Reset submission state
      isSubmitting = false;
      
      // Restore button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit';
      }
    }
  }
  
  // Enhanced security validation
  function validateFormSecurity(form) {
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    
    // Check honeypot field
    if (data.website && data.website.trim() !== '') {
      showError('Form submission blocked by security check');
      return false;
    }
    
    // Check time-based validation
    if (data.formStartTime) {
      const startTime = parseInt(data.formStartTime);
      const currentTime = Date.now();
      const timeDiff = currentTime - startTime;
      
      // If form was filled in less than 3 seconds, likely a bot
      if (timeDiff < 3000) {
        showError('Form submitted too quickly. Please take your time filling out the form.');
        return false;
      }
    }
    

    
    return true;
  }
  
  // Show success message
  function showSuccess() {
    const form = document.getElementById('secureForm');
    if (form) {
      form.innerHTML = \`
        <div style="text-align: center; padding: 40px 20px;">
          <div style="font-size: 48px; margin-bottom: 20px;">✅</div>
          <h2 style="color: #10b981; margin: 0 0 15px 0; font-size: 24px;">Thank You!</h2>
          <p style="color: #6b7280; margin: 0; font-size: 16px; line-height: 1.5;">
            Your message has been received successfully.<br>
            We'll get back to you soon.
          </p>
        </div>
      \`;
    }
    
    // Notify parent window of successful submission
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'form-success',
        message: 'Form submitted successfully'
      }, '*');
    }
  }
  
  // Show error message
  function showError(message) {
    let errorDiv = document.querySelector('.form-message.error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'form-message error';
      errorDiv.style.cssText = 'background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; padding: 15px; border-radius: 6px; margin: 20px 0; font-weight: 500;';
      const form = document.getElementById('secureForm');
      if (form) {
        form.insertBefore(errorDiv, form.firstChild);
      }
    }
    
    errorDiv.textContent = message;
    
    // Notify parent window of error
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'form-error',
        error: message
      }, '*');
    }
  }
  
  // Show phone number error message
  function showPhoneError() {
    const phoneError = document.getElementById('phone-error');
    if (phoneError) {
      phoneError.style.display = 'block';
      // Hide the error after 3 seconds
      setTimeout(() => {
        phoneError.style.display = 'none';
      }, 3000);
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFormSecurity);
  } else {
    initializeFormSecurity();
  }
})();
</script>`;

    return html;
  }

  // Generate iframe embed code instead of complex HTML
  function generateIframeEmbedCode(formId: string) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL?.includes("localhost")
      ? "http://localhost:3001"
      : "https://api.dojotrack.com";

    return `<!-- Embed this code on your website -->
<iframe 
  src="${baseUrl}/api/supabase/forms/embed/${formId}" 
  width="100%" 
  height="400" 
  frameborder="0" 
  style="border-radius: 8px;"
  title="Contact Form">
</iframe>

<script>
  // Optional: Auto-resize iframe to fit content
  window.addEventListener('message', function(event) {
    if (event.origin !== '${baseUrl}') return;
    
    if (event.data.type === 'iframe-resize') {
      const iframe = document.querySelector('iframe[src*="${formId}"]');
      if (iframe) {
        iframe.style.height = event.data.height + 'px';
      }
    } else if (event.data.type === 'form-success') {
      // Optional: Handle successful form submission
      // Form submitted successfully
    } else if (event.data.type === 'form-error') {
      // Optional: Handle form errors
      // Form submission error occurred
    }
  });
</script>`;
  }

  function generateStyledIframeCode(
    formId: string,
    formName: string,
    options: Record<string, string>
  ) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL?.includes("localhost")
      ? "http://localhost:3001"
      : "https://api.dojotrack.com";

    const params = new URLSearchParams(options).toString();
    const srcUrl = `${baseUrl}/api/supabase/forms/embed/${formId}${
      params ? "?" + params : ""
    }`;

    return `<!-- Styled embed code -->
<iframe 
  src="${srcUrl}" 
  width="100%" 
  height="400" 
  frameborder="0" 
  style="border-radius: 8px;"
  title="${formName}">
</iframe>

<script>
  window.addEventListener('message', function(event) {
    if (event.origin !== '${baseUrl}') return;
    if (event.data.type === 'iframe-resize') {
      const iframe = document.querySelector('iframe[src*="${formId}"]');
      if (iframe) iframe.style.height = event.data.height + 'px';
    }
  });
</script>`;
  }

  const generatedHTML = generateFormHTML(formFields);
  


  useEffect(() => {
    fetchCsrfToken();
    const fetchForms = async () => {
      const authToken = getAuthToken();
      if (!studioUser?.studio_id || !authToken) return;

      try {
        setError(null); // Clear any previous errors
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/forms/${studioUser.studio_id}?page=1&limit=50`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!response.ok) {
          if (response.status === 429) {
            throw new Error('Rate limit exceeded. Please try again later.');
          }
          throw new Error("Failed to fetch forms");
        }
        const result = await response.json();
        setForms(result.data || []);
      } catch (err) {
        logSecurityEvent('FORMS_FETCH_ERROR', { 
          error: err instanceof Error ? err.message : 'Unknown error',
          studioId: studioUser?.studio_id 
        });
        setError(err instanceof Error ? err.message : "Failed to fetch forms");
      } finally {
        setLoading(false);
      }
    };

    fetchForms();

    // Fetch auto appointment SMS setting
    const fetchAutoAppointmentSmsSetting = async () => {
      const authToken = getAuthToken();
      if (!studioUser?.studio_id || !authToken) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setAutoAppointmentSmsEnabled(data?.auto_appointment_sms_enabled ?? true);
        }
      } catch (error) {
        // Default to enabled if fetch fails
        setAutoAppointmentSmsEnabled(true);
      }
    };

    fetchAutoAppointmentSmsSetting();
  }, [studioUser?.studio_id, getAuthToken]);

  // Add a derived variable for the form limit
  const formLimitReached = forms.length >= formLimit;

  // Update openCreateModal to prevent opening if limit reached
  const openCreateModal = () => {
    if (formLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${formLimit} forms per account. Please delete an existing form to create a new one.`,
        title: "Form Limit Reached",
      });
      return;
    }
    setNewForm({ name: "", type: "", html: "" });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  // In the create modal, prevent submission if limit is reached (shouldn't happen, but for safety)
  const handleCreateFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${formLimit} forms per account. Please delete an existing form to create a new one.`,
        title: "Form Limit Reached",
      });
      setShowCreateModal(false);
      return;
    }
    await handleCreateForm({ ...newForm, html: generatedHTML });
    setShowCreateModal(false);
  };

  const handleCreateForm = async (formData: Partial<FormRow>) => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation (matching PublicWaiver.tsx approach)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'createForm' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Enhanced input validation
    if (!validateInput(formData.name || '', 'formName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'formName', value: formData.name });
      setNotification({
        show: true,
        message: "Invalid form name",
        title: "Error",
      });
      return;
    }

    if (!validateInput(formData.html || '', 'formHtml')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'formHtml', value: formData.html?.substring(0, 100) + '...' });
      setNotification({
        show: true,
        message: "Invalid form HTML",
        title: "Error",
      });
      return;
    }

    // Check form limit
    if (forms.length >= formLimit) {
      setNotification({
        show: true,
        message: `Maximum number of forms (${formLimit}) reached`,
        title: "Error",
      });
      return;
    }

    setLastSubmissionTime(now);

    try {
      setError(null); // Clear any previous errors
      const { name, html } = formData;
      if (!html) {
        throw new Error("Form HTML is required");
      }

      // Enhanced input sanitization
      const sanitizedName = sanitizeInput(name || '', 'formName');
      const sanitizedHtml = sanitizeInput(html, 'formHtml');

      // Replace placeholders in the HTML
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://api.dojotrack.com";
      const htmlWithPlaceholders = sanitizedHtml
        .replace("PLACEHOLDER_API_URL", backendUrl);

      const payload = {
        name: sanitizedName,
        html: htmlWithPlaceholders,
        studio_id: studioUser.studio_id,
        type: "contact", // Default form type
      };


      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/forms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        // Try to get detailed error message
        try {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || 'Failed to create form');
        } catch (parseError) {
          throw new Error('Failed to create form');
        }
      }

      const newForm = await response.json();
      setForms((prev) => [...prev, newForm]);
      setNotification({
        show: true,
        message: "Form created successfully",
        title: "Success",
      });
    } catch (err) {
      logSecurityEvent('FORM_CREATION_ERROR', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        formData: { name: formData.name, hasHtml: !!formData.html }
      });
      setError(err instanceof Error ? err.message : "Failed to create form");
      setNotification({
        show: true,
        message: "Failed to create form",
        title: "Error",
      });
    }
  };

  const handleDeleteForm = async (form: FormRow) => {
    setFormToDelete(form);
  };

  const confirmDelete = async () => {
    if (!formToDelete) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation (matching PublicWaiver.tsx approach)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteForm', formId: formToDelete.id });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    setLastSubmissionTime(now);

    try {
      setError(null); // Clear any previous errors
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/forms/${studioUser.studio_id}/${formToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        // Try to get detailed error message
        try {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || 'Failed to delete form');
        } catch (parseError) {
          throw new Error('Failed to delete form');
        }
      }

      setForms((prev) => prev.filter((form) => form.id !== formToDelete.id));
      setNotification({
        show: true,
        message: "Form deleted successfully",
        title: "Success",
      });
      setFormToDelete(null);
    } catch (err) {
      logSecurityEvent('FORM_DELETION_ERROR', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        formId: formToDelete.id 
      });
      setError(err instanceof Error ? err.message : "Failed to delete form");
      setNotification({
        show: true,
        message: "Failed to delete form",
        title: "Error",
      });
    }
  };

  const cancelDelete = () => {
    setFormToDelete(null);
  };



  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white text-gray-900`;
  };

  return (
    <PlanGatedPage
      capability="websiteForms"
      title="Website Forms"
      subtitle="Manage your studio forms and templates"
    >
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Forms
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your studio forms and templates
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className={`inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-fit xl:w-auto${formLimitReached ? " opacity-50 cursor-not-allowed" : ""}`}
            disabled={formLimitReached}
            title={formLimitReached ? `You can only create up to ${formLimit} forms per account.` : ""}
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
            <span className="pointer-events-none">{`Create Form ${forms.length}/${formLimit}`}</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-4">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Loading forms...
              </div>
            </div>
          </div>
        ) : error &&
          !viewingForm &&
          !showCreateModal ? ( // Hide errors when any modal is open
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 relative">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-0 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Created
                        </th>
                        <th
                          scope="col"
                          className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                        >
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {forms.map((form) => (
                        <tr key={form.id}>
                          <td className="whitespace-nowrap py-4 pl-0 text-sm font-medium text-gray-900 dark:text-white">
                            {DOMPurify.sanitize(form.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              {new Date(form.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              onClick={() => setViewingForm(form)}
                              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2 mr-2"
                              title="View Embed Code"
                            >
                              <PencilIcon className="h-5 w-5 pointer-events-none" />
                            </button>

                            <button
                              onClick={() => handleDeleteForm(form)}
                              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
                              title="Delete Form"
                            >
                              <TrashIcon className="h-5 w-5 pointer-events-none" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {viewingForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-8 rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  {viewingForm.name} - Embed Code
                </h3>
                <button
                  onClick={() => setViewingForm(null)}
                  className="p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition"
                >
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>

              {/* Iframe Embed Code Section */}
              <div className="mb-6">
                {/* Basic Embed Code */}
                {/* <div className="mb-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                    📋 Basic Embed Code
                  </h4>
                  <pre className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 overflow-x-auto whitespace-pre-wrap text-xs text-gray-800 dark:text-gray-200 font-mono max-h-32 overflow-y-auto">
                    {generateIframeEmbedCode(viewingForm.id)}
                  </pre>
                </div> */}

                {/* Simple Styling Options */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-3">
                    🎨 Customize Your Form
                  </h4>

                  {/* Theme and Color Selector */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Theme Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Theme
                        </label>
                        <div className="flex gap-2">
                          <button
                            className={`flex-1 px-3 py-2 text-sm border rounded transition ${
                              previewTheme === "light"
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                            }`}
                            onClick={() => setPreviewTheme("light")}
                          >
                            <span className="pointer-events-none">☀️ Light</span>
                          </button>
                          <button
                            className={`flex-1 px-3 py-2 text-sm border rounded transition ${
                              previewTheme === "dark"
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                : "border-gray-300 dark:border-gray-600 bg-gray-700 dark:bg-gray-800 text-white hover:bg-gray-800 dark:hover:bg-gray-700"
                            }`}
                            onClick={() => setPreviewTheme("dark")}
                          >
                            <span className="pointer-events-none">🌙 Dark</span>
                          </button>
                        </div>
                      </div>

                      {/* Button Color Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Button Colors
                        </label>

                        {/* Custom Color Inputs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Button Background
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={previewColor}
                                onChange={(e) =>
                                  setPreviewColor(e.target.value)
                                }
                                className={getInputClassName(
                                  "w-12 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                )}
                              />
                              <input
                                type="text"
                                value={previewColor}
                                onChange={(e) => {
                                  if (
                                    /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)
                                  ) {
                                    setPreviewColor(e.target.value);
                                  }
                                }}
                                placeholder="#667eea"
                                className={getInputClassName(
                                  "flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                                )}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Button Text
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={previewButtonText}
                                onChange={(e) =>
                                  setPreviewButtonText(e.target.value)
                                }
                                className={getInputClassName(
                                  "w-12 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                                )}
                              />
                              <input
                                type="text"
                                value={previewButtonText}
                                onChange={(e) => {
                                  if (
                                    /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)
                                  ) {
                                    setPreviewButtonText(e.target.value);
                                  }
                                }}
                                placeholder="#ffffff"
                                className={getInputClassName(
                                  "flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Preset Colors - Quick Selection */}
                        <div>
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            Quick Presets
                          </div>
                          <div className="grid grid-cols-8 gap-2">
                            {[
                              {
                                name: "Blue",
                                color: "#3b82f6",
                              },
                              {
                                name: "Green",
                                color: "#10b981",
                              },
                              {
                                name: "Purple",
                                color: "#8b5cf6",
                              },
                              {
                                name: "Red",
                                color: "#ef4444",
                              },
                              {
                                name: "Orange",
                                color: "#f59e0b",
                              },
                              {
                                name: "Pink",
                                color: "#ec4899",
                              },
                              {
                                name: "Teal",
                                color: "#14b8a6",
                              },
                              {
                                name: "Yellow",
                                color: "#eab308",
                              },
                            ].map((colorOption) => (
                              <button
                                key={colorOption.name}
                                className={`w-full h-6 rounded border border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition`}
                                style={{
                                  backgroundColor: colorOption.color,
                                }}
                                title={colorOption.name}
                                onClick={() =>
                                  setPreviewColor(colorOption.color)
                                }
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Background Color Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Background Colors
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Light Theme Background */}
                        <div>
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            ☀️ Light Theme Background
                          </div>
                          {/* Custom Color Input */}
                          <div className="flex gap-2 mb-3">
                            <input
                              type="color"
                              value={lightBgColor}
                              onChange={(e) => setLightBgColor(e.target.value)}
                              className={getInputClassName(
                                "w-12 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                              )}
                            />
                            <input
                              type="text"
                              value={lightBgColor}
                              onChange={(e) => {
                                if (
                                  /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)
                                ) {
                                  setLightBgColor(e.target.value);
                                }
                              }}
                              placeholder="#ffffff"
                              className={getInputClassName(
                                "flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                              )}
                            />
                          </div>
                          {/* Presets */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Quick Presets
                          </div>
                          <div className="grid grid-cols-6 gap-2">
                            {[
                              {
                                name: "White",
                                color: "#ffffff",
                              },
                              {
                                name: "Light Gray",
                                color: "#f9fafb",
                              },
                              {
                                name: "Light Blue",
                                color: "#eff6ff",
                              },
                              {
                                name: "Light Green",
                                color: "#f0fdf4",
                              },
                              {
                                name: "Light Purple",
                                color: "#faf5ff",
                              },
                              {
                                name: "Cream",
                                color: "#fefbf3",
                              },
                            ].map((bgOption) => (
                              <button
                                key={bgOption.name}
                                className="w-full h-6 rounded border border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition"
                                style={{
                                  backgroundColor: bgOption.color,
                                }}
                                title={bgOption.name}
                                onClick={() => setLightBgColor(bgOption.color)}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Dark Theme Background */}
                        <div>
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            🌙 Dark Theme Background
                          </div>
                          {/* Custom Color Input */}
                          <div className="flex gap-2 mb-3">
                            <input
                              type="color"
                              value={darkBgColor}
                              onChange={(e) => setDarkBgColor(e.target.value)}
                              className={getInputClassName(
                                "w-12 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                              )}
                            />
                            <input
                              type="text"
                              value={darkBgColor}
                              onChange={(e) => {
                                if (
                                  /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)
                                ) {
                                  setDarkBgColor(e.target.value);
                                }
                              }}
                              placeholder="#1a1a1a"
                              className={getInputClassName(
                                "flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                              )}
                            />
                          </div>
                          {/* Presets */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Quick Presets
                          </div>
                          <div className="grid grid-cols-6 gap-2">
                            {[
                              {
                                name: "Dark Gray",
                                color: "#1a1a1a",
                              },
                              {
                                name: "Charcoal",
                                color: "#111827",
                              },
                              {
                                name: "Dark Blue",
                                color: "#0f172a",
                              },
                              {
                                name: "Dark Green",
                                color: "#064e3b",
                              },
                              {
                                name: "Black",
                                color: "#000000",
                              },
                              {
                                name: "Dark Red",
                                color: "#7f1d1d",
                              },
                            ].map((bgOption) => (
                              <button
                                key={bgOption.name}
                                className="w-full h-6 rounded border border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition"
                                style={{
                                  backgroundColor: bgOption.color,
                                }}
                                title={bgOption.name}
                                onClick={() => setDarkBgColor(bgOption.color)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-600">
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              generateStyledIframeCode(
                                viewingForm.id,
                                viewingForm.name,
                                {
                                  theme: previewTheme,
                                  primaryColor: previewColor,
                                  textColor: previewButtonText,
                                  backgroundColor:
                                    previewTheme === "light"
                                      ? lightBgColor
                                      : darkBgColor,
                                }
                              )
                            );
                            setNotification({
                              show: true,
                              message: "iFrame copied to clipboard",
                              title: "Success",
                            });
                          } catch (error) {
                            setNotification({
                              show: true,
                              message: "Failed to copy to clipboard",
                              title: "Error",
                            });
                          }
                        }}
                      >
                        <span className="pointer-events-none">📋 Copy Current Embed Code</span>
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Customize your form above, then copy the embed code
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Section */}
              <div className="mb-6">
                <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📱 Live Preview
                </div>
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <iframe
                    src={`${
                      process.env.REACT_APP_BACKEND_URL ||
                      "http://localhost:3001"
                    }/api/supabase/forms/embed/${
                      viewingForm.id
                    }?theme=${previewTheme}&primaryColor=${encodeURIComponent(
                      previewColor
                    )}&textColor=${encodeURIComponent(
                      previewButtonText
                    )}&backgroundColor=${encodeURIComponent(
                      previewTheme === "light" ? lightBgColor : darkBgColor
                    )}`}
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ borderRadius: "8px" }}
                    title="Form Preview"
                    key={`${previewTheme}-${previewColor}-${previewButtonText}-${lightBgColor}-${darkBgColor}`} // Force re-render when settings change
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-4 sm:p-8 rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  Create New Form
                </h3>
                <button
                  type="button"
                  className="p-2 rounded-md bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={closeCreateModal}
                >
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
              <form onSubmit={handleCreateFormSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Form Name
                  </label>
                  <input
                    type="text"
                    className={getInputClassName(
                      "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition dark:bg-gray-800 dark:text-white"
                    )}
                    value={newForm.name}
                    onChange={(e) =>
                      setNewForm((f) => ({
                        ...f,
                        name: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Fields
                  </label>
                  <div className="flex flex-wrap gap-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                    {Object.entries(formFields).map(([key, value]) => (
                      <label
                        key={key}
                        className={`flex items-center gap-2 text-gray-700 dark:text-gray-300 ${
                          key === "smsConsent" ? "opacity-75" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) =>
                            setFormFields((f) => ({
                              ...f,
                              [key]: e.target.checked,
                            }))
                          }
                          disabled={key === "smsConsent"}
                          className="accent-indigo-500 h-4 w-4 rounded disabled:cursor-not-allowed"
                        />
                        {key === "smsConsent"
                          ? "SMS Consent Checkbox (Required)"
                          : key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <p>
                      <strong>Security:</strong> All forms automatically include honeypot, time-based bot protection, and CSRF tokens (invisible to users).
                    </p>
                    <p className="text-red-600 dark:text-red-400">
                      <strong>Legal Requirement:</strong> SMS Consent is mandatory for TCPA compliance. This ensures you have documented consent before sending SMS messages to form submitters.
                    </p>
                  </div>
                  {formFields.smsConsent && autoAppointmentSmsEnabled && (
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <div className="text-xs text-blue-800 dark:text-blue-300">
                        <strong>💡 SMS Consent Feature:</strong> When enabled, leads who check this box will automatically receive a welcome SMS with a link to book an appointment, helping convert leads into students faster. Requires Twilio integration in Settings → Integrations.
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col xl:flex-row xl:justify-end gap-2 pt-4">
                  <button
                    type="button"
                    className="w-full xl:w-auto px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition font-semibold"
                    onClick={closeCreateModal}
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="submit"
                    className="w-full xl:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition font-semibold shadow"
                  >
                    <span className="pointer-events-none">Create Form</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}



        {/* Delete Confirmation Modal */}
        {formToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={cancelDelete}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Form
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete "{formToDelete.name}"?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </PlanGatedPage>
  );
}
